import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import {
  ArrowLeft,
  Users,
  GraduationCap,
  UserCheck,
  TrendingDown,
  Award,
  LayoutGrid,
  BookOpen,
  Heart,
  Briefcase,
} from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';
import {
  getClasswiseEnrollment,
  getEnrollmentTrend,
  getHighestEnrollmentSchools,
  getLowEnrollmentSchools,
  getParentServiceGroupData,
  getCastConseGroupData,
  getMinorityGroupData,
} from '../../services/api';
import { Loader2, RefreshCw } from 'lucide-react';



export default function EnrollmentAnalysis() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<'class' | 'gender' | 'stream' | 'social' | 'minority' | 'parent'>('class');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolsData, setSchoolsData] = useState<{ top: any[]; bottom: any[] }>({ top: [], bottom: [] });
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [parentGroupData, setParentGroupData] = useState<any>(null);
  const [socialGroupData, setSocialGroupData] = useState<any>(null);
  const [minorityGroupData, setMinorityGroupData] = useState<any>(null);
    const [selectedSchool, setSelectedSchool] = useState<{
      schoolID: number;
      schoolName: string;
      infraData?: any;
      tabLabel?: string;
    } | null>(null);

  const fetchSchoolsData = async (view: 'class' | 'gender' | 'stream' | 'social' | 'minority' | 'parent') => {
    try {
      setSchoolsLoading(true);
      const wiseParam =
        view === 'gender'   ? 'overallgenderdistribution' :
        view === 'stream'   ? 'classwiseenrollment' :
        view === 'social'   ? 'classwiseenrollment' :
        view === 'minority' ? 'classwiseenrollment' :
        view === 'parent'   ? 'classwiseenrollment' :
        'classwiseenrollment';

      const [topSchools, bottomSchools] = await Promise.all([
        getHighestEnrollmentSchools(wiseParam, 'desc'),
        getLowEnrollmentSchools(wiseParam),
      ]);

      setSchoolsData({ top: topSchools || [], bottom: bottomSchools || [] });
    } catch (err: any) {
      console.error('Failed to fetch schools data:', err);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rawClasswiseData, trendData, rawParentGroupData, rawSocialGroupData, rawMinorityGroupData] = await Promise.all([
        getClasswiseEnrollment(),
        getEnrollmentTrend(5),
        getParentServiceGroupData().catch(() => null),
        getCastConseGroupData().catch(() => null),
        getMinorityGroupData().catch(() => null),
      ]);

      // Normalize classwiseData to always be an array
      // API returns { breakdown: [...], grandTotals: {...} }
      const classwiseData: any[] = Array.isArray(rawClasswiseData)
        ? rawClasswiseData
        : Array.isArray(rawClasswiseData?.breakdown)
          ? rawClasswiseData.breakdown
          : [];

      // Fetch initial schools data for class view
      await fetchSchoolsData('class');

      if (rawParentGroupData) {
        setParentGroupData(rawParentGroupData);
      }
      if (rawSocialGroupData) {
        setSocialGroupData(rawSocialGroupData);
      }
      if (rawMinorityGroupData) {
        setMinorityGroupData(rawMinorityGroupData);
      }

      // Calculate totals from classwise data
      const totalPmshri = classwiseData.reduce((sum: number, item: any) => sum + (item.pmShriEnrollment || 0), 0) || 0;
      const totalNonPmshri = classwiseData.reduce((sum: number, item: any) => sum + (item.nonPmShriEnrollment || 0), 0) || 0;
      const totalStudents = totalPmshri + totalNonPmshri;

      // Extract enrollment counts from the most recent trend entry
      // Normalize trendData into array so object responses don't vanish
      // The latest year entry has enrollment sub-fields as objects {total, totalBoys, totalGirls}
      // Older entries may have them as plain numbers — find the richest entry
      const trendArray = Array.isArray(trendData)
        ? trendData
        : trendData ? [trendData] : [];
      const latestTrend =
        // prefer entry whose totalEnrollment is an object (has .total)
        trendArray.find((t: any) => t?.enrollment?.totalEnrollment && typeof t.enrollment.totalEnrollment === 'object')
        // fallback: highest year number
        ?? (trendArray.length > 0
              ? trendArray.reduce((best: any, curr: any) =>
                  Number(curr?.year) > Number(best?.year ?? 0) ? curr : best,
                  trendArray[0]
                )
              : undefined)
        // still fallback
        ?? trendArray[trendArray.length - 1]
        ?? trendArray[0];
      const totalEnrollmentCount = latestTrend?.enrollment?.totalEnrollment?.total ?? totalStudents;
      const pmShriTotal          = latestTrend?.enrollment?.pmShriEnrollment?.total ?? totalPmshri;
      const nonPmShriTotal       = latestTrend?.enrollment?.nonPmShriEnrollment?.total ?? totalNonPmshri;
      const enrollmentBoys       = latestTrend?.enrollment?.totalEnrollment?.totalBoys ?? 0;
      const enrollmentGirls      = latestTrend?.enrollment?.totalEnrollment?.totalGirls ?? 0;

      // Helper: aggregate PM-SHRI vs Non-PMSHRI by class codes
      const groupByLevel = (codes: number[]) => {
        const filtered = classwiseData?.filter((item: any) => codes.includes(item.classCode)) || [];
        const pmshri   = filtered.reduce((sum: number, item: any) => sum + (item.pmShriEnrollment    || 0), 0);
        const nonPmshri = filtered.reduce((sum: number, item: any) => sum + (item.nonPmShriEnrollment || 0), 0);
        return { pmshri, nonPmshri, total: pmshri + nonPmshri };
      };

      // Stage-based grouping aligned to ClassWiseStudents structure
      const stages = {
        foundation:      groupByLevel([-3, -2, -1, 1, 2]),   // Balvatika 1-3 (codes may vary)
        preparatory:     groupByLevel([3, 4, 5]),
        middle:          groupByLevel([6, 7, 8]),
        secondary:       groupByLevel([9,	10,	11,	12]),
      };

      // Extract stream data for Class 11 and Class 12 using streamCode/streamName from API
      type StreamEntry = { pmshri: number; nonPmshri: number; total: number };
      const makeStream = (): Record<string, StreamEntry> => ({
        Science:    { pmshri: 0, nonPmshri: 0, total: 0 },
        Commerce:   { pmshri: 0, nonPmshri: 0, total: 0 },
        Humanities: { pmshri: 0, nonPmshri: 0, total: 0 },
        Vocational: { pmshri: 0, nonPmshri: 0, total: 0 },
      });
      const streamByClass: Record<number, Record<string, StreamEntry>> = { 11: makeStream(), 12: makeStream() };
      classwiseData.forEach((item: any) => {
        const cc = item.classCode as number;
        const sn = item.streamName as string;
        if ((cc === 11 || cc === 12) && sn && sn !== 'N/A' && sn !== 'General') {
          const target = streamByClass[cc];
          if (!target[sn]) target[sn] = { pmshri: 0, nonPmshri: 0, total: 0 };
          target[sn].pmshri    += item.pmShriEnrollment    || 0;
          target[sn].nonPmshri += item.nonPmShriEnrollment || 0;
          target[sn].total     += item.totalEnrollment     || 0;
        }
      });

      setData({
        summary: {
          totalStudents: totalEnrollmentCount,
          totalSections: 0,
          avgStudentsPerSection: 0,
          boys:          enrollmentBoys,
          girls:         enrollmentGirls,
          others:        0,
          pmShriTotal,
          nonPmShriTotal,
        },
        stages,
        streamByClass,
        yearlyTrend: Array.isArray(trendData) ? trendData : [],
        rawData: [classwiseData, trendData],
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch schools data when view changes
  useEffect(() => {
    if (data) {
      fetchSchoolsData(activeView);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="w-full max-w-7xl mx-auto px-4 pt-4">
          <Breadcrumb />
        </div>
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading enrollment data...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="w-full max-w-7xl mx-auto px-4 pt-4">
          <Breadcrumb />
        </div>
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) return null;

  const addPercentage = (items: { category: string; pmshriCount: number; nonPmshriCount: number; total: number }[]) =>
    items.map(item => ({
      ...item,
      pmshriPercentage: item.total > 0 ? (item.pmshriCount / item.total) * 100 : 0
    }));

  // Get all individual classes from raw API data (de-duped by classCode)
  const getAllClassesData = () => {
    const [classwiseData] = data?.rawData || [[], []];
    if (!classwiseData || !Array.isArray(classwiseData)) return [];
    // Aggregate all stream rows (N/A + General for Cl 1-10; Science/Commerce/Humanities/Vocational for Cl 11-12)
    const seen = new Map<number, any>();
    classwiseData.forEach((item: any) => {
      if (!seen.has(item.classCode)) {
        seen.set(item.classCode, {
          category: item.className?.replace(/\s*\([^)]*\)/, '').trim() || `Class ${item.classCode}`,
          pmshriCount:    0,
          nonPmshriCount: 0,
          total:          0,
        });
      }
      const entry = seen.get(item.classCode)!;
      entry.pmshriCount    += item.pmShriEnrollment    || 0;
      entry.nonPmshriCount += item.nonPmShriEnrollment || 0;
      entry.total          += item.totalEnrollment     || 0;
    });
    // Sort: pre-primary (negative codes) first, then Class 1–12
    return Array.from(seen.entries())
      .sort(([a], [b]) => a - b)
      .map(([, v]) => v);
  };

  // Stage-wise chart data
  const stageData = addPercentage([
    { category: 'Foundational Stage',      pmshriCount: data?.stages?.foundation?.pmshri || 0,      nonPmshriCount: data?.stages?.foundation?.nonPmshri || 0,      total: data?.stages?.foundation?.total || 0 },
    { category: 'Preparatory Stage',     pmshriCount: data?.stages?.preparatory?.pmshri || 0,     nonPmshriCount: data?.stages?.preparatory?.nonPmshri || 0,     total: data?.stages?.preparatory?.total || 0 },
    { category: 'Middle Stage',          pmshriCount: data?.stages?.middle?.pmshri || 0,          nonPmshriCount: data?.stages?.middle?.nonPmshri || 0,          total: data?.stages?.middle?.total || 0 },
    { category: 'Secondary Stage',       pmshriCount: data?.stages?.secondary?.pmshri || 0,       nonPmshriCount: data?.stages?.secondary?.nonPmshri || 0,       total: data?.stages?.secondary?.total || 0 },
    { category: 'Sr. Secondary Stage',   pmshriCount: data?.stages?.seniorSecondary?.pmshri || 0, nonPmshriCount: data?.stages?.seniorSecondary?.nonPmshri || 0, total: data?.stages?.seniorSecondary?.total || 0 },
  ]);

  const classWiseData = addPercentage(getAllClassesData());

  // Aggregate gender from school-level data if available
  const allSchools = [...schoolsData.top, ...schoolsData.bottom];
  const totalBoys   = allSchools.reduce((s, it) => s + (+(it?.Gender?.[0] ?? it?.data?.boys   ?? 0)), 0);
  const totalGirls  = allSchools.reduce((s, it) => s + (+(it?.Gender?.[1] ?? it?.data?.girls  ?? 0)), 0);
  const totalOthers = allSchools.reduce((s, it) => s + (+(it?.Gender?.[2] ?? 0)), 0);

  // Prefer real totals from summary (sourced from trend API) over school-list aggregation
  const genderBoys   = data?.summary?.boys   || totalBoys;
  const genderGirls  = data?.summary?.girls  || totalGirls;
  const genderOthers = data?.summary?.others || totalOthers;
  const genderData = [
    { name: 'Boys',  value: genderBoys },
    { name: 'Girls', value: genderGirls },
    ...(genderOthers > 0 ? [{ name: 'Other', value: genderOthers }] : []),
  ].filter(d => d.value > 0);

  // Social category aggregate — prefer dedicated API data, fall back to school-level aggregation
  const socialAgg = (() => {
    if (socialGroupData) {
      return {
        General: +(socialGroupData['General']?.totalEnrollment?.total || 0),
        OBC_CL:  +(socialGroupData['OBC']?.totalEnrollment?.total || socialGroupData['OBC_CL']?.totalEnrollment?.total  || socialGroupData['OBC CL']?.totalEnrollment?.total  || 0),
        OBC_NCL: +(socialGroupData['OBC_NCL']?.totalEnrollment?.total || socialGroupData['OBC NCL']?.totalEnrollment?.total || 0),
        SC:      +(socialGroupData['SC']?.totalEnrollment?.total      || 0),
        ST:      +(socialGroupData['ST']?.totalEnrollment?.total      || 0),
      };
    }
    return allSchools.reduce(
      (acc, it) => {
        const sc = it?.SocialCategory || {};
        acc.General  += +(sc.General  || 0);
        acc.OBC_CL   += +(sc.OBC_CL  || 0);
        acc.OBC_NCL  += +(sc.OBC_NCL || 0);
        acc.SC       += +(sc.SC      || 0);
        acc.ST       += +(sc.ST      || 0);
        return acc;
      },
      { General: 0, OBC_CL: 0, OBC_NCL: 0, SC: 0, ST: 0 }
    );
  })();
  const socialData = [
    { name: 'General',   value: socialAgg.General },
    { name: 'OBC (CL)',  value: socialAgg.OBC_CL },
    { name: 'OBC (NCL)', value: socialAgg.OBC_NCL },
    { name: 'SC',        value: socialAgg.SC },
    { name: 'ST',        value: socialAgg.ST },
  ].filter(d => d.value > 0);

  // Minority group — prefer dedicated API data, fall back to school-level aggregation
  const MINORITY_KEYS = ['Muslim', 'Christian', 'Sikh', 'Buddhist', 'Parsi', 'Jain', 'Others'] as const;
  type MinorityKey = typeof MINORITY_KEYS[number];
  const mg_api = minorityGroupData?.MinorityGroups || {};
  const minorityAgg = MINORITY_KEYS.reduce((acc, k) => {
    const entry = mg_api[k];
    acc[k] = entry
      ? (+(entry.pmShri?.total || 0)) + (+(entry.nonPmShri?.total || 0))
      : +(allSchools.reduce((s, it) => s + +(it?.MinorityGroup?.[k] || 0), 0));
    return acc;
  }, { Muslim: 0, Christian: 0, Sikh: 0, Buddhist: 0, Parsi: 0, Jain: 0, Others: 0, NA: 0 } as Record<MinorityKey | 'NA', number>);
  // NA handled separately (not shown in pie)
  const na_entry = mg_api['NA'];
  minorityAgg['NA'] = na_entry
    ? (+(na_entry.pmShri?.total || 0)) + (+(na_entry.nonPmShri?.total || 0))
    : 0;

  const minorityData = [
    { name: 'Muslim',    value: minorityAgg.Muslim },
    { name: 'Christian', value: minorityAgg.Christian },
    { name: 'Sikh',      value: minorityAgg.Sikh },
    { name: 'Buddhist',  value: minorityAgg.Buddhist },
    { name: 'Parsi',     value: minorityAgg.Parsi },
    { name: 'Jain',      value: minorityAgg.Jain },
    { name: 'Others',    value: minorityAgg.Others },
  ].filter(d => d.value > 0);

  // Parent service category aggregate — prefer dedicated API data, fall back to school-level aggregation
  const parentCatAgg = (() => {
    if (parentGroupData) {
      return {
        Project_IHL: +(parentGroupData['Category: 0 (IHL)']?.total || 0),
        Cat1:        +(parentGroupData['Category: 1']?.total        || 0),
        Cat2:        +(parentGroupData['Category: 2']?.total        || 0),
        Cat3:        +(parentGroupData['Category: 3']?.total        || 0),
        Cat4:        +(parentGroupData['Category: 4']?.total        || 0),
        Cat5:        +(parentGroupData['Category: 5']?.total        || 0),
      };
    }
    return allSchools.reduce(
      (acc, it) => {
        const pc = it?.ParentsServiceCategory || {};
        acc.Project_IHL += +(pc.Project_IHL || 0);
        acc.Cat1        += +(pc.Category1   || 0);
        acc.Cat2        += +(pc.Category2   || 0);
        acc.Cat3        += +(pc.Category3   || 0);
        acc.Cat4        += +(pc.Category4   || 0);
        acc.Cat5        += +(pc.Category5   || 0);
        return acc;
      },
      { Project_IHL: 0, Cat1: 0, Cat2: 0, Cat3: 0, Cat4: 0, Cat5: 0 }
    );
  })();

  // Boys/Girls breakdown for parent categories (from dedicated API)
  const parentCatGender = parentGroupData ? {
    Project_IHL: { boys: +(parentGroupData['Category: 0 (IHL)']?.totalBoys || 0), girls: +(parentGroupData['Category: 0 (IHL)']?.totalGirls || 0) },
    Cat1:        { boys: +(parentGroupData['Category: 1']?.totalBoys        || 0), girls: +(parentGroupData['Category: 1']?.totalGirls        || 0) },
    Cat2:        { boys: +(parentGroupData['Category: 2']?.totalBoys        || 0), girls: +(parentGroupData['Category: 2']?.totalGirls        || 0) },
    Cat3:        { boys: +(parentGroupData['Category: 3']?.totalBoys        || 0), girls: +(parentGroupData['Category: 3']?.totalGirls        || 0) },
    Cat4:        { boys: +(parentGroupData['Category: 4']?.totalBoys        || 0), girls: +(parentGroupData['Category: 4']?.totalGirls        || 0) },
    Cat5:        { boys: +(parentGroupData['Category: 5']?.totalBoys        || 0), girls: +(parentGroupData['Category: 5']?.totalGirls        || 0) },
  } : null;

  const parentCatData = [
    { name: 'Project / IHL', value: parentCatAgg.Project_IHL },
    { name: 'Category I',    value: parentCatAgg.Cat1 },
    { name: 'Category II',   value: parentCatAgg.Cat2 },
    { name: 'Category III',  value: parentCatAgg.Cat3 },
    { name: 'Category IV',   value: parentCatAgg.Cat4 },
    { name: 'Category V',    value: parentCatAgg.Cat5 },
  ].filter(d => d.value > 0);

  const STREAMS = ['Science', 'Commerce', 'Humanities', 'Vocational'] as const;
  const class11Streams = addPercentage(
    STREAMS.map(s => ({
      category: s,
      pmshriCount:    data?.streamByClass?.[11]?.[s]?.pmshri    || 0,
      nonPmshriCount: data?.streamByClass?.[11]?.[s]?.nonPmshri || 0,
      total:          data?.streamByClass?.[11]?.[s]?.total     || 0,
    }))
  );
  const class12Streams = addPercentage(
    STREAMS.map(s => ({
      category: s,
      pmshriCount:    data?.streamByClass?.[12]?.[s]?.pmshri    || 0,
      nonPmshriCount: data?.streamByClass?.[12]?.[s]?.nonPmshri || 0,
      total:          data?.streamByClass?.[12]?.[s]?.total     || 0,
    }))
  );
  const combinedStreams = addPercentage(
    STREAMS.map(s => ({
      category: s,
      pmshriCount:    (data?.streamByClass?.[11]?.[s]?.pmshri    || 0) + (data?.streamByClass?.[12]?.[s]?.pmshri    || 0),
      nonPmshriCount: (data?.streamByClass?.[11]?.[s]?.nonPmshri || 0) + (data?.streamByClass?.[12]?.[s]?.nonPmshri || 0),
      total:          (data?.streamByClass?.[11]?.[s]?.total     || 0) + (data?.streamByClass?.[12]?.[s]?.total     || 0),
    }))
  );
  const streamData = combinedStreams; // alias kept for any legacy references

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <GraduationCap className="w-7 h-7" />
              Enrollment Analysis
            </h1>
            <p className="text-gray-600">
              Comprehensive view of student enrollment across classes, gender, and streams
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analytics
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Enrollment"
            value={(data?.summary?.totalStudents ?? 0).toLocaleString()}
            icon={<Users className="w-6 h-6" />}
            color="purple"
            subtitle="All KVS Schools"
          />
          <StatCard
            title="PM-SHRI Enrollment"
            value={(data?.summary?.pmShriTotal ?? 0).toLocaleString()}
            icon={<Award className="w-6 h-6" />}
            color="green"
            subtitle="PM-SHRI Schools"
          />
          <StatCard
            title="Non PM-SHRI"
            value={(data?.summary?.nonPmShriTotal ?? 0).toLocaleString()}
            icon={<GraduationCap className="w-6 h-6" />}
            color="blue"
            subtitle="Non PM-SHRI Schools"
          />
          <StatCard
            title="Boys"
            value={(data?.summary?.boys ?? 0).toLocaleString()}
            icon={<UserCheck className="w-6 h-6" />}
            color="blue"
            subtitle={`${((data?.summary?.boys ?? 0) / (data?.summary?.totalStudents || 1) * 100).toFixed(1)}%`}
          />
          <StatCard
            title="Girls"
            value={(data?.summary?.girls ?? 0).toLocaleString()}
            icon={<UserCheck className="w-6 h-6" />}
            color="pink"
            subtitle={`${((data?.summary?.girls ?? 0) / (data?.summary?.totalStudents || 1) * 100).toFixed(1)}%`}
          />
        </div>

        {/* View Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'class',    label: 'Class-wise',          icon: LayoutGrid },
            { id: 'gender',   label: 'Gender',               icon: Users },
            { id: 'stream',   label: 'Stream-wise (XI-XII)', icon: GraduationCap },
            { id: 'social',   label: 'Social Category',      icon: BookOpen },
            { id: 'minority', label: 'Minority Group',        icon: Heart },
            { id: 'parent',  label: 'Parents Service Category', icon: Briefcase },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  activeView === tab.id
                    ? 'bg-purple-100 border-purple-300 text-purple-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Charts */}
        {activeView === 'class' && (
          <div className="space-y-6 mb-6">
            {/* Stage-wise summary table */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Stage-wise Enrollment</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Stage</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-500">Classes</th>
                      <th className="text-center py-2 px-3 font-semibold text-green-600">PM-SHRI</th>
                      <th className="text-center py-2 px-3 font-semibold text-blue-600">Non PM-SHRI</th>
                      <th className="text-center py-2 px-3 font-semibold text-purple-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { label: 'Foundational Stage',    sub: 'Balvatika (-3, -2, -1) I II',    key: 'foundation' },
                      { label: 'Preparatory Stage',   sub: 'Class III - V',     key: 'preparatory' },
                      { label: 'Middle Stage',        sub: 'Class VI - VIII',    key: 'middle' },
                      { label: 'Secondary Stage',     sub: 'Class IX – XII',  key: 'secondary' },
                    ].map(row => {
                      const s = data?.stages?.[row.key] || {};
                      return (
                        <tr key={row.key} className="hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium text-gray-800">{row.label}<br/><span className="text-xs text-gray-400">{row.sub}</span></td>
                          <td className="py-2 px-3 text-center text-gray-500">{row.sub.split('–').join('–')}</td>
                          <td className="py-2 px-3 text-center text-green-600 font-medium">{(s.pmshri   || 0).toLocaleString()}</td>
                          <td className="py-2 px-3 text-center text-blue-600  font-medium">{(s.nonPmshri || 0).toLocaleString()}</td>
                          <td className="py-2 px-3 text-center text-purple-600 font-bold">{(s.total    || 0).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Per-class chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Class-wise Enrollment - PM-SHRI vs Non PM-SHRI</h3>
              <div className="overflow-x-auto">
                <div style={{ minWidth: `${classWiseData.length * 80}px`, width: '100%' }}>
                  <ComparisonBarChart
                    data={classWiseData}
                    title=""
                    xAxisLabel="Class Level"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DistributionPieChart
                data={classWiseData.map(d => ({ name: d.category, value: d.total }))}
                title="Class-wise Distribution"
              />
              <DistributionPieChart
                data={stageData.map(d => ({ name: d.category, value: d.total }))}
                title="Stage-wise Distribution"
              />
            </div>
          </div>
        )}

        {activeView === 'gender' && (
          <div className="space-y-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DistributionPieChart data={genderData} title="Overall Gender Distribution" />
              {/* Gender by stage bar */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Gender Ratio by Stage</h3>
                <div className="space-y-4">
                  {[
                    { level: 'Foundational',    total: data?.stages?.foundation?.total      || 0 },
                    { level: 'Preparatory',   total: data?.stages?.preparatory?.total     || 0 },
                    { level: 'Middle',        total: data?.stages?.middle?.total          || 0 },
                    { level: 'Secondary',     total: data?.stages?.secondary?.total       || 0 },
                  ].map((item) => {
                    const boysRatio  = genderBoys  > 0 ? genderBoys  / (genderBoys + genderGirls) : 0.52;
                    const girlsRatio = genderGirls > 0 ? genderGirls / (genderBoys + genderGirls) : 0.48;
                    const boys   = Math.floor(item.total * boysRatio);
                    const girls  = item.total - boys;
                    const total  = boys + girls;
                    const girlPct = total > 0 ? ((girls / total) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={item.level}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{item.level}</span>
                          <span className="text-gray-500">{girlPct}% Girls</span>
                        </div>
                        <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
                          <div className="bg-blue-500" style={{ width: `${total > 0 ? (boys / total) * 100 : 50}%` }} />
                          <div className="bg-pink-500" style={{ width: `${total > 0 ? (girls / total) * 100 : 50}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-6 mt-4 text-sm">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /> Boys</span>
                  <span className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-pink-500" /> Girls</span>
                </div>
              </div>
            </div>
            {/* Per-school gender from top schools */}
            {schoolsData.top.some((s: any) => s?.Gender) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Gender Detail — Top Enrollment Schools</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-gray-600">School</th>
                        <th className="text-center py-2 px-3 text-blue-600">Boys</th>
                        <th className="text-center py-2 px-3 text-pink-600">Girls</th>
                        <th className="text-center py-2 px-3 text-gray-500">Other</th>
                        <th className="text-center py-2 px-3 text-purple-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {schoolsData.top.map((item: any, idx: number) => {
                        const name  = item?.School?.Name     || item?.school?.schoolName || 'N/A';
                        const boys  = +(item?.Gender?.[0] ?? item?.data?.boys   ?? 0);
                        const girls = +(item?.Gender?.[1] ?? item?.data?.girls  ?? 0);
                        const other = +(item?.Gender?.[2] ?? 0);
                        const tot   = +(item?.TotalStudents  ?? item?.data?.totalEnrollment ?? (boys + girls + other));
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium text-gray-800">{name}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{boys.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-pink-600">{girls.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-500">{other.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-purple-600 font-bold">{tot.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeView === 'stream' && (() => {
          const STREAM_COLORS: Record<string, { bar: string; text: string }> = {
            Science:    { bar: 'bg-blue-500',   text: 'text-blue-600'   },
            Commerce:   { bar: 'bg-green-500',  text: 'text-green-600'  },
            Humanities: { bar: 'bg-orange-500', text: 'text-orange-600' },
            Vocational: { bar: 'bg-indigo-500', text: 'text-indigo-600' },
          };
          const renderStreamSection = (
            label: string,
            rows: ReturnType<typeof addPercentage>,
            accentBg: string
          ) => {
            const sectionTotal = rows.reduce((s, r) => s + r.total, 0);
            return (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className={`px-6 py-4 border-b border-gray-200 ${accentBg}`}>
                  <h3 className="text-base font-semibold text-gray-800">{label}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Total: {sectionTotal.toLocaleString()} students</p>
                </div>
                <div className="p-5 space-y-3">
                  {rows.filter(r => r.total > 0).map(row => {
                    const c = STREAM_COLORS[row.category] || { bar: 'bg-gray-400', text: 'text-gray-600' };
                    const pct = sectionTotal > 0 ? ((row.total / sectionTotal) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={row.category}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className={`font-medium ${c.text}`}>{row.category}</span>
                          <span className="text-gray-500">{row.total.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full ${c.bar} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="flex gap-4 mt-0.5 text-xs">
                          <span className="text-green-600">PM-SHRI: {row.pmshriCount.toLocaleString()}</span>
                          <span className="text-blue-600">Non PM-SHRI: {row.nonPmshriCount.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                  {rows.every(r => r.total === 0) && (
                    <p className="text-sm text-gray-400 text-center py-4">No stream data available</p>
                  )}
                </div>
              </div>
            );
          };

          return (
            <div className="space-y-6 mb-6">
              {/* Class XI & XII side-by-side stream breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderStreamSection('Class XI — Stream-wise Enrollment', class11Streams, 'bg-purple-50')}
                {renderStreamSection('Class XII — Stream-wise Enrollment', class12Streams, 'bg-indigo-50')}
              </div>

              {/* Per-class pie charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DistributionPieChart
                  data={class11Streams.filter(d => d.total > 0).map(d => ({ name: d.category, value: d.total }))}
                  title="Class XI — Stream Distribution"
                />
                <DistributionPieChart
                  data={class12Streams.filter(d => d.total > 0).map(d => ({ name: d.category, value: d.total }))}
                  title="Class XII — Stream Distribution"
                />
              </div>

              {/* Combined bar chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Combined (XI + XII) — PM-SHRI vs Non PM-SHRI by Stream</h3>
                <ComparisonBarChart
                  data={combinedStreams.filter(d => d.total > 0)}
                  title=""
                  xAxisLabel="Stream"
                />
              </div>

              {/* Detailed comparison table */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Stream-wise Detailed Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-2 px-3 font-semibold text-gray-600">Stream</th>
                        <th className="text-center py-2 px-3 font-semibold text-purple-600" colSpan={3}>Class XI</th>
                        <th className="text-center py-2 px-3 font-semibold text-indigo-600" colSpan={3}>Class XII</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-700" colSpan={3}>Combined</th>
                      </tr>
                      <tr className="border-b border-gray-200 text-xs text-gray-500 bg-gray-50">
                        <th className="py-1 px-3" />
                        <th className="py-1 px-3 text-center text-green-600">PM-SHRI</th>
                        <th className="py-1 px-3 text-center text-blue-600">Non PM-SHRI</th>
                        <th className="py-1 px-3 text-center text-purple-700 font-semibold">Total</th>
                        <th className="py-1 px-3 text-center text-green-600">PM-SHRI</th>
                        <th className="py-1 px-3 text-center text-blue-600">Non PM-SHRI</th>
                        <th className="py-1 px-3 text-center text-indigo-700 font-semibold">Total</th>
                        <th className="py-1 px-3 text-center text-green-600">PM-SHRI</th>
                        <th className="py-1 px-3 text-center text-blue-600">Non PM-SHRI</th>
                        <th className="py-1 px-3 text-center text-gray-800 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {STREAMS.map(s => {
                        const c11  = class11Streams.find(r => r.category === s)!;
                        const c12  = class12Streams.find(r => r.category === s)!;
                        const comb = combinedStreams.find(r => r.category === s)!;
                        const sc   = STREAM_COLORS[s] || { text: 'text-gray-700', bar: '' };
                        return (
                          <tr key={s} className="hover:bg-gray-50">
                            <td className={`py-2 px-3 font-medium ${sc.text}`}>{s}</td>
                            <td className="py-2 px-3 text-center text-green-600">{c11.pmshriCount.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{c11.nonPmshriCount.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-purple-700 font-medium">{c11.total.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-600">{c12.pmshriCount.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{c12.nonPmshriCount.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-indigo-700 font-medium">{c12.total.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-600">{comb.pmshriCount.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{comb.nonPmshriCount.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-800 font-bold">{comb.total.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                      {(() => {
                        const t11   = class11Streams.reduce((a, r) => ({ pm: a.pm + r.pmshriCount, npm: a.npm + r.nonPmshriCount, tot: a.tot + r.total }), { pm: 0, npm: 0, tot: 0 });
                        const t12   = class12Streams.reduce((a, r) => ({ pm: a.pm + r.pmshriCount, npm: a.npm + r.nonPmshriCount, tot: a.tot + r.total }), { pm: 0, npm: 0, tot: 0 });
                        const tcomb = combinedStreams.reduce((a, r)  => ({ pm: a.pm + r.pmshriCount, npm: a.npm + r.nonPmshriCount, tot: a.tot + r.total }), { pm: 0, npm: 0, tot: 0 });
                        return (
                          <tr className="bg-gray-50 font-semibold">
                            <td className="py-2 px-3 text-gray-800">Total</td>
                            <td className="py-2 px-3 text-center text-green-700">{t11.pm.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700">{t11.npm.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-purple-700">{t11.tot.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-700">{t12.pm.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700">{t12.npm.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-indigo-700">{t12.tot.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-700">{tcomb.pm.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700">{tcomb.npm.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-900">{tcomb.tot.toLocaleString()}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {activeView === 'social' && (
          <div className="space-y-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {socialData.length > 0
                ? <DistributionPieChart data={socialData} title="Social Category Distribution" />
                : <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center text-gray-400">No social category data available</div>
              }
              {/* Breakdown bars */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Category Breakdown</h3>
                <div className="space-y-3">
                  {[
                    { label: 'General',    color: 'bg-blue-500',   val: socialAgg.General,  key: 'General'  },
                    { label: 'OBC (CL)',   color: 'bg-green-500',  val: socialAgg.OBC_CL,   key: 'OBC_CL'  },
                    { label: 'OBC (NCL)', color: 'bg-yellow-500', val: socialAgg.OBC_NCL,  key: 'OBC_NCL' },
                    { label: 'SC',         color: 'bg-orange-500', val: socialAgg.SC,        key: 'SC'       },
                    { label: 'ST',         color: 'bg-red-500',    val: socialAgg.ST,        key: 'ST'       },
                  ].map(row => {
                    const grandTotal = socialAgg.General + socialAgg.OBC_CL + socialAgg.OBC_NCL + socialAgg.SC + socialAgg.ST;
                    const pct = grandTotal > 0 ? ((row.val / grandTotal) * 100).toFixed(1) : '0.0';
                    // Map row keys to API response keys: 'OBC' in API is OBC_CL, 'OBC_NCL' is OBC_NCL
                    const apiKey = row.key === 'OBC_CL' 
                      ? (socialGroupData?.['OBC'] ? 'OBC' : (socialGroupData?.['OBC_CL'] ? 'OBC_CL' : 'OBC CL'))
                      : row.key === 'OBC_NCL' 
                      ? (socialGroupData?.['OBC_NCL'] ? 'OBC_NCL' : 'OBC NCL')
                      : row.key;
                    const entry = socialGroupData?.[apiKey] || {};
                    const boys  = +(entry.totalEnrollment?.totalBoys  || 0);
                    const girls = +(entry.totalEnrollment?.totalGirls || 0);
                    return (
                      <div key={row.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{row.label}</span>
                          <span className="text-gray-500">{row.val.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full ${row.color} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                        {socialGroupData && boys + girls > 0 && (
                          <div className="flex gap-3 mt-0.5 text-xs text-gray-400">
                            <span className="text-blue-500">👦 {boys.toLocaleString()}</span>
                            <span className="text-pink-500">👧 {girls.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Summary table from dedicated API */}
            {socialGroupData && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Social Category — Overall Summary</h3>
                  {(() => {
                    const allEntries = Object.values(socialGroupData) as any[];
                    const pmTotal = allEntries.reduce((s, v) => s + +(v?.pmShriEnrollment?.total || 0), 0);
                    const nonTotal = allEntries.reduce((s, v) => s + +(v?.nonPmShriEnrollment?.total || 0), 0);
                    const tot = allEntries.reduce((s, v) => s + +(v?.totalEnrollment?.total || 0), 0);
                    const grandTotal = socialGroupData?.['GrandTotal']?.totalEnrollment?.total || tot;
                    const unclassified = grandTotal - (pmTotal + nonTotal);
                    return unclassified > 0 ? (
                      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                        <span className="font-medium">⚠️ Note:</span> {unclassified.toLocaleString()} students ({((unclassified/grandTotal)*100).toFixed(1)}%) have unclassified PM-SHRI status
                      </div>
                    ) : null;
                  })()}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-600">Category</th>
                        <th className="text-center py-2 px-3 font-semibold text-green-600">PM-SHRI Boys</th>
                        <th className="text-center py-2 px-3 font-semibold text-green-600">PM-SHRI Girls</th>
                        <th className="text-center py-2 px-3 font-semibold text-green-700">PM-SHRI Total</th>
                        <th className="text-center py-2 px-3 font-semibold text-blue-600">Non PM-SHRI Boys</th>
                        <th className="text-center py-2 px-3 font-semibold text-blue-600">Non PM-SHRI Girls</th>
                        <th className="text-center py-2 px-3 font-semibold text-blue-700">Non PM-SHRI Total</th>
                        <th className="text-center py-2 px-3 font-semibold text-purple-600">Total</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-500">% Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        { key: 'General',  apiKey: 'General',  label: 'General',   color: 'text-blue-600'   },
                        { key: 'OBC_CL',   apiKey: 'OBC_CL',   label: 'OBC (CL)',  color: 'text-green-600'  },
                        { key: 'OBC_NCL',  apiKey: 'OBC_NCL',  label: 'OBC (NCL)', color: 'text-yellow-600' },
                        { key: 'SC',       apiKey: 'SC',        label: 'SC',        color: 'text-orange-600' },
                        { key: 'ST',       apiKey: 'ST',        label: 'ST',        color: 'text-red-600'    },
                      ].map(row => {
                        // Map row keys to API response keys: 'OBC' in API is OBC_CL, 'OBC_NCL' is OBC_NCL
                        const resolvedKey = row.apiKey === 'OBC_CL'
                          ? (socialGroupData?.['OBC'] ? 'OBC' : (socialGroupData[row.apiKey] ? row.apiKey : 'OBC CL'))
                          : row.apiKey === 'OBC_NCL'
                          ? (socialGroupData[row.apiKey] ? row.apiKey : 'OBC NCL')
                          : (socialGroupData[row.apiKey] ? row.apiKey : row.apiKey);
                        const entry  = socialGroupData[resolvedKey] || {};
                        const pmShri    = entry.pmShriEnrollment    || {};
                        const nonPmShri = entry.nonPmShriEnrollment || {};
                        const total     = entry.totalEnrollment     || {};
                        const grandTotal = socialAgg.General + socialAgg.OBC_CL + socialAgg.OBC_NCL + socialAgg.SC + socialAgg.ST;
                        const pct = grandTotal > 0 ? (((+(total.total || 0)) / grandTotal) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={row.key} className="hover:bg-gray-50">
                            <td className={`py-2 px-3 font-medium ${row.color}`}>{row.label}</td>
                            <td className="py-2 px-3 text-center text-green-600">{(+(pmShri.totalBoys   || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-600">{(+(pmShri.totalGirls  || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-700 font-medium">{(+(pmShri.total || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{(+(nonPmShri.totalBoys  || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{(+(nonPmShri.totalGirls || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700 font-medium">{(+(nonPmShri.total || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-purple-600 font-bold">{(+(total.total || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-500">{pct}%</td>
                          </tr>
                        );
                      })}
                      {/* Unclassified row */}
                      {(() => {
                        const allEntries = Object.values(socialGroupData) as any[];
                        const pmTotal = allEntries.reduce((s, v) => s + +(v?.pmShriEnrollment?.total || 0), 0);
                        const nonTotal = allEntries.reduce((s, v) => s + +(v?.nonPmShriEnrollment?.total || 0), 0);
                        const tot = allEntries.reduce((s, v) => s + +(v?.totalEnrollment?.total || 0), 0);
                        const grandTotal = socialGroupData?.['GrandTotal']?.totalEnrollment?.total || tot;
                        const unclassified = grandTotal - (pmTotal + nonTotal);
                        if (unclassified <= 0) return null;
                        const pct = ((unclassified / grandTotal) * 100).toFixed(1);
                        return (
                          <tr className="bg-amber-50">
                            <td className="py-2 px-3 font-medium text-amber-700">Unclassified</td>
                            <td className="py-2 px-3 text-center text-gray-500">-</td>
                            <td className="py-2 px-3 text-center text-gray-500">-</td>
                            <td className="py-2 px-3 text-center text-gray-500">-</td>
                            <td className="py-2 px-3 text-center text-gray-500">-</td>
                            <td className="py-2 px-3 text-center text-gray-500">-</td>
                            <td className="py-2 px-3 text-center text-gray-500">-</td>
                            <td className="py-2 px-3 text-center text-amber-700 font-bold">{unclassified.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-amber-700">{pct}%</td>
                          </tr>
                        );
                      })()}
                      {/* Grand total row */}
                      {(() => {
                        const allEntries = Object.values(socialGroupData) as any[];
                        const pmBoys   = allEntries.reduce((s, v) => s + +(v?.pmShriEnrollment?.totalBoys    || 0), 0);
                        const pmGirls  = allEntries.reduce((s, v) => s + +(v?.pmShriEnrollment?.totalGirls   || 0), 0);
                        const pmTotal  = allEntries.reduce((s, v) => s + +(v?.pmShriEnrollment?.total        || 0), 0);
                        const nonBoys  = allEntries.reduce((s, v) => s + +(v?.nonPmShriEnrollment?.totalBoys  || 0), 0);
                        const nonGirls = allEntries.reduce((s, v) => s + +(v?.nonPmShriEnrollment?.totalGirls || 0), 0);
                        const nonTotal = allEntries.reduce((s, v) => s + +(v?.nonPmShriEnrollment?.total      || 0), 0);
                        const tot      = allEntries.reduce((s, v) => s + +(v?.totalEnrollment?.total          || 0), 0);
                        const grandTotal = socialGroupData?.['GrandTotal']?.totalEnrollment?.total || tot;
                        return (
                          <tr className="bg-gray-50 font-semibold">
                            <td className="py-2 px-3 text-gray-800">Grand Total</td>
                            <td className="py-2 px-3 text-center text-green-700">{pmBoys.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-700">{pmGirls.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-700">{pmTotal.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700">{nonBoys.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700">{nonGirls.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700">{nonTotal.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-purple-700">{grandTotal.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-500">100%</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Per-school detail */}
            {schoolsData.top.some((s: any) => s?.SocialCategory) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Social Category — Top Enrollment Schools</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-gray-600">School</th>
                        <th className="text-center py-2 px-3">General</th>
                        <th className="text-center py-2 px-3">OBC-CL</th>
                        <th className="text-center py-2 px-3">OBC-NCL</th>
                        <th className="text-center py-2 px-3">SC</th>
                        <th className="text-center py-2 px-3">ST</th>
                        <th className="text-center py-2 px-3 text-purple-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {schoolsData.top.map((item: any, idx: number) => {
                        const sc   = item?.SocialCategory || {};
                        const name = item?.School?.Name || item?.school?.schoolName || 'N/A';
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium text-gray-800">{name}</td>
                            <td className="py-2 px-3 text-center">{+(sc.General  || 0)}</td>
                            <td className="py-2 px-3 text-center">{+(sc.OBC_CL   || 0)}</td>
                            <td className="py-2 px-3 text-center">{+(sc.OBC_NCL  || 0)}</td>
                            <td className="py-2 px-3 text-center">{+(sc.SC       || 0)}</td>
                            <td className="py-2 px-3 text-center">{+(sc.ST       || 0)}</td>
                            <td className="py-2 px-3 text-center text-purple-600 font-bold">{+(sc.Total || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeView === 'minority' && (() => {
          const ROWS = [
            { label: 'Muslim',    key: 'Muslim',    color: 'bg-green-500',   textColor: 'text-green-700'  },
            { label: 'Christian', key: 'Christian', color: 'bg-blue-500',    textColor: 'text-blue-700'   },
            { label: 'Sikh',      key: 'Sikh',      color: 'bg-orange-500',  textColor: 'text-orange-700' },
            { label: 'Buddhist',  key: 'Buddhist',  color: 'bg-yellow-500',  textColor: 'text-yellow-700' },
            { label: 'Parsi',     key: 'Parsi',     color: 'bg-red-500',     textColor: 'text-red-700'    },
            { label: 'Jain',      key: 'Jain',      color: 'bg-purple-500',  textColor: 'text-purple-700' },
            { label: 'Others',    key: 'Others',    color: 'bg-gray-400',    textColor: 'text-gray-600'   },
          ];
          const ts = minorityGroupData?.TotalSummary;
          const minorityOnlyTotal = ROWS.reduce((s, r) => s + (minorityAgg as any)[r.key], 0);
          const grandTotal = +(ts?.overall?.total || 0) || minorityOnlyTotal;
          return (
            <div className="space-y-6 mb-6">
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Overall Total Enrollment</h4>
                  <p className="text-2xl font-bold text-[#6A1786]">{(+(ts?.overall?.total || 0)).toLocaleString()}</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span className="text-blue-600">Boys: {(+(ts?.overall?.totalBoys || 0)).toLocaleString()}</span>
                    <span className="text-pink-600">Girls: {(+(ts?.overall?.totalGirls || 0)).toLocaleString()}</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">PM-SHRI Enrollment</h4>
                  <p className="text-2xl font-bold text-green-700">{(+(ts?.pmShri?.total || 0)).toLocaleString()}</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span className="text-blue-600">Boys: {(+(ts?.pmShri?.totalBoys || 0)).toLocaleString()}</span>
                    <span className="text-pink-600">Girls: {(+(ts?.pmShri?.totalGirls || 0)).toLocaleString()}</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Minority Students</h4>
                  <p className="text-2xl font-bold text-amber-700">{minorityOnlyTotal.toLocaleString()}</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span className="text-gray-500">
                      {grandTotal > 0 ? ((minorityOnlyTotal / grandTotal) * 100).toFixed(1) : '0.0'}% of total enrollment
                    </span>
                  </div>
                </div>
              </div>

              {/* Pie chart + breakdown bars */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {minorityData.length > 0
                  ? <DistributionPieChart data={minorityData} title="Minority Group Distribution" />
                  : <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center text-gray-400">No minority group data available</div>
                }
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Minority Group Breakdown</h3>
                  <div className="space-y-3">
                    {ROWS.map(row => {
                      const val   = (minorityAgg as any)[row.key] as number;
                      const pct   = minorityOnlyTotal > 0 ? ((val / minorityOnlyTotal) * 100).toFixed(1) : '0.0';
                      const entry = mg_api[row.key];
                      const boys  = entry ? (+(entry.pmShri?.boys || 0)) + (+(entry.nonPmShri?.boys || 0)) : 0;
                      const girls = entry ? (+(entry.pmShri?.girls || 0)) + (+(entry.nonPmShri?.girls || 0)) : 0;
                      return (
                        <div key={row.key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className={`font-medium ${row.textColor}`}>{row.label}</span>
                            <span className="text-gray-500">{val.toLocaleString()} ({pct}%)</span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${row.color} rounded-full`} style={{ width: `${pct}%` }} />
                          </div>
                          {(boys > 0 || girls > 0) && (
                            <div className="flex gap-3 mt-0.5 text-xs text-gray-400">
                              <span className="text-blue-500">👦 {boys.toLocaleString()}</span>
                              <span className="text-pink-500">👧 {girls.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Detailed breakdown table */}
              {minorityGroupData && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Minority Group — Detailed Breakdown</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-xs">
                          <th className="text-left py-2 px-3 font-semibold text-gray-600" rowSpan={2}>Group</th>
                          <th className="text-center py-2 px-3 font-semibold text-green-700 border-l border-gray-200" colSpan={3}>PM-SHRI</th>
                          <th className="text-center py-2 px-3 font-semibold text-blue-700 border-l border-gray-200" colSpan={3}>Non PM-SHRI</th>
                          <th className="text-center py-2 px-3 font-semibold text-purple-700 border-l border-gray-200" rowSpan={2}>Grand Total</th>
                          <th className="text-center py-2 px-3 font-semibold text-gray-500" rowSpan={2}>%</th>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
                          <th className="py-1 px-3 text-center border-l border-gray-200">Boys</th>
                          <th className="py-1 px-3 text-center">Girls</th>
                          <th className="py-1 px-3 text-center">Total</th>
                          <th className="py-1 px-3 text-center border-l border-gray-200">Boys</th>
                          <th className="py-1 px-3 text-center">Girls</th>
                          <th className="py-1 px-3 text-center">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...ROWS, { label: 'NA (Not Applicable)', key: 'NA', color: 'bg-gray-300', textColor: 'text-gray-500' }].map(row => {
                          const entry = mg_api[row.key];
                          if (!entry) return null;
                          const pm  = entry.pmShri    || {};
                          const npm = entry.nonPmShri || {};
                          const rowTotal = (+(pm.total || 0)) + (+(npm.total || 0));
                          const pct = grandTotal > 0 ? ((rowTotal / grandTotal) * 100).toFixed(1) : '0.0';
                          return (
                            <tr key={row.key} className="hover:bg-gray-50">
                              <td className={`py-2 px-3 font-medium ${row.textColor}`}>{row.label}</td>
                              <td className="py-2 px-3 text-center text-green-600 border-l border-gray-100">{(+(pm.boys || 0)).toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-pink-600">{(+(pm.girls || 0)).toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-green-700 font-semibold">{(+(pm.total || 0)).toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-blue-600 border-l border-gray-100">{(+(npm.boys || 0)).toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-pink-600">{(+(npm.girls || 0)).toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-blue-700 font-semibold">{(+(npm.total || 0)).toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-purple-700 font-bold border-l border-gray-100">{rowTotal.toLocaleString()}</td>
                              <td className="py-2 px-3 text-center text-gray-500">{pct}%</td>
                            </tr>
                          );
                        })}
                        {/* Grand total row */}
                        {ts && (
                          <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                            <td className="py-2 px-3 text-gray-800">Grand Total</td>
                            <td className="py-2 px-3 text-center text-green-700 border-l border-gray-100">{(+(ts.pmShri?.totalBoys || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-pink-700">{(+(ts.pmShri?.totalGirls || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-green-800">{(+(ts.pmShri?.total || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-blue-700 border-l border-gray-100">
                              {((+(ts.overall?.totalBoys || 0)) - (+(ts.pmShri?.totalBoys || 0))).toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-center text-pink-700">
                              {((+(ts.overall?.totalGirls || 0)) - (+(ts.pmShri?.totalGirls || 0))).toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-center text-blue-800">
                              {((+(ts.overall?.total || 0)) - (+(ts.pmShri?.total || 0))).toLocaleString()}
                            </td>
                            <td className="py-2 px-3 text-center text-purple-800 border-l border-gray-100">{(+(ts.overall?.total || 0)).toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-500">100%</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {activeView === 'parent' && (
          <div className="space-y-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {parentCatData.length > 0
                ? <DistributionPieChart data={parentCatData} title="Parent Service Category Distribution" />
                : <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center text-gray-400">No parent service category data available</div>
              }
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Category Breakdown</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Project / IHL', color: 'bg-blue-500',   val: parentCatAgg.Project_IHL, genderKey: 'Project_IHL' as const },
                    { label: 'Category I (Central Govt.)',    color: 'bg-green-500',  val: parentCatAgg.Cat1, genderKey: 'Cat1' as const },
                    { label: 'Category II (Central Autonomous)',   color: 'bg-yellow-500', val: parentCatAgg.Cat2, genderKey: 'Cat2' as const },
                    { label: 'Category III (State Govt.)',  color: 'bg-orange-500', val: parentCatAgg.Cat3, genderKey: 'Cat3' as const },
                    { label: 'Category IV (State Autonomous)',   color: 'bg-red-500',    val: parentCatAgg.Cat4, genderKey: 'Cat4' as const },
                    { label: 'Category V (Other)',    color: 'bg-purple-500', val: parentCatAgg.Cat5, genderKey: 'Cat5' as const },
                  ].map(row => {
                    const total = parentCatAgg.Project_IHL + parentCatAgg.Cat1 + parentCatAgg.Cat2 + parentCatAgg.Cat3 + parentCatAgg.Cat4 + parentCatAgg.Cat5;
                    const pct   = total > 0 ? ((row.val / total) * 100).toFixed(1) : '0.0';
                    const gender = parentCatGender?.[row.genderKey];
                    return (
                      <div key={row.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{row.label}</span>
                          <span className="text-gray-500">{row.val.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full ${row.color} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                        {gender && (
                          <div className="flex gap-3 mt-0.5 text-xs text-gray-400">
                            <span className="text-blue-500">👦 {gender.boys.toLocaleString()}</span>
                            <span className="text-pink-500">👧 {gender.girls.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Summary table from dedicated API */}
            {parentGroupData && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Parent Service Category — Overall Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-600">Category</th>
                        <th className="text-center py-2 px-3 font-semibold text-blue-600">Boys</th>
                        <th className="text-center py-2 px-3 font-semibold text-pink-600">Girls</th>
                        <th className="text-center py-2 px-3 font-semibold text-purple-600">Total</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-500">% Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        { key: 'Category: 0 (IHL)', label: 'Project / IHL',                   color: 'text-blue-600' },
                        { key: 'Category: 1',        label: 'Category I (Central Govt.)',       color: 'text-green-600' },
                        { key: 'Category: 2',        label: 'Category II (Central Autonomous)', color: 'text-yellow-600' },
                        { key: 'Category: 3',        label: 'Category III (State Govt.)',       color: 'text-orange-600' },
                        { key: 'Category: 4',        label: 'Category IV (State Autonomous)',   color: 'text-red-600' },
                        { key: 'Category: 5',        label: 'Category V (Other)',               color: 'text-purple-600' },
                      ].map(row => {
                        const entry = parentGroupData[row.key] || {};
                        const total   = +(entry.total      || 0);
                        const boys    = +(entry.totalBoys  || 0);
                        const girls   = +(entry.totalGirls || 0);
                        const grandTotal = parentCatAgg.Project_IHL + parentCatAgg.Cat1 + parentCatAgg.Cat2 + parentCatAgg.Cat3 + parentCatAgg.Cat4 + parentCatAgg.Cat5;
                        const pct = grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={row.key} className="hover:bg-gray-50">
                            <td className={`py-2 px-3 font-medium ${row.color}`}>{row.label}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{boys.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-pink-600">{girls.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-purple-600 font-bold">{total.toLocaleString()}</td>
                            <td className="py-2 px-3 text-center text-gray-500">{pct}%</td>
                          </tr>
                        );
                      })}
                      {/* Grand total row */}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="py-2 px-3 text-gray-800">Grand Total</td>
                        <td className="py-2 px-3 text-center text-blue-700">
                          {(Object.values(parentGroupData) as any[]).reduce((s: number, v: any) => s + +(v?.totalBoys  || 0), 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-center text-pink-700">
                          {(Object.values(parentGroupData) as any[]).reduce((s: number, v: any) => s + +(v?.totalGirls || 0), 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-center text-purple-700">
                          {(parentCatAgg.Project_IHL + parentCatAgg.Cat1 + parentCatAgg.Cat2 + parentCatAgg.Cat3 + parentCatAgg.Cat4 + parentCatAgg.Cat5).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-center text-gray-500">100%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Per-school detail */}
            {schoolsData.top.some((s: any) => s?.ParentsServiceCategory) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Parents Service Category — Top Enrollment Schools</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-gray-600">School</th>
                        <th className="text-center py-2 px-3 text-blue-600">Project / IHL</th>
                        <th className="text-center py-2 px-3 text-green-600">Category I (Central Govt.)</th>
                        <th className="text-center py-2 px-3 text-yellow-600">Category II (Central Autonomous)</th>
                        <th className="text-center py-2 px-3 text-orange-600">Category III (State Govt.)</th>
                        <th className="text-center py-2 px-3 text-red-600">Category IV (State Autonomous)</th>
                        <th className="text-center py-2 px-3 text-purple-600">Category V (Other)</th>
                        <th className="text-center py-2 px-3 text-gray-700 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {schoolsData.top.map((item: any, idx: number) => {
                        const pc   = item?.ParentsServiceCategory || {};
                        const name = item?.School?.Name || item?.school?.schoolName || 'N/A';
                        const tot  = +(pc.Total || 0) || [
                          +(pc.Project_IHL || 0), +(pc.Category1 || 0), +(pc.Category2 || 0),
                          +(pc.Category3 || 0), +(pc.Category4 || 0), +(pc.Category5 || 0)
                        ].reduce((a, b) => a + b, 0);
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium text-gray-800">{name}</td>
                            <td className="py-2 px-3 text-center text-blue-600">{+(pc.Project_IHL || 0)}</td>
                            <td className="py-2 px-3 text-center text-green-600">{+(pc.Category1 || 0)}</td>
                            <td className="py-2 px-3 text-center text-yellow-600">{+(pc.Category2 || 0)}</td>
                            <td className="py-2 px-3 text-center text-orange-600">{+(pc.Category3 || 0)}</td>
                            <td className="py-2 px-3 text-center text-red-600">{+(pc.Category4 || 0)}</td>
                            <td className="py-2 px-3 text-center text-purple-600">{+(pc.Category5 || 0)}</td>
                            <td className="py-2 px-3 text-center font-bold text-gray-700">{tot.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Yearly Trend */}
        {/* <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Enrollment Trend (Last 4 Years)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Year</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-green-600">PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-blue-600">Non PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-purple-600">Total</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Growth</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.yearlyTrend || []).map((row: any, index: number) => {
                  const pmshri = row?.enrollment?.pmShriEnrollment?.total ?? row?.enrollment?.pmShriEnrollment ?? 0;
                  const nonPmshri = row?.enrollment?.nonPmShriEnrollment?.total ?? row?.enrollment?.nonPmShriEnrollment ?? 0;
                  const total = row?.enrollment?.totalEnrollment?.total ?? row?.enrollment?.totalEnrollment ?? (pmshri + nonPmshri);
                  const prevRow = index > 0 ? data.yearlyTrend[index - 1] : null;
                  const prevTotalRaw = prevRow?.enrollment?.totalEnrollment;
                  const prevTotal = (typeof prevTotalRaw === 'object' ? prevTotalRaw?.total : prevTotalRaw) ?? 0;
                  const growth = prevTotal > 0 ? (((total - prevTotal) / prevTotal) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={row.year} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.year}</td>
                      <td className="py-3 px-4 text-sm text-center text-green-600 font-medium">{pmshri.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-center text-blue-600 font-medium">{nonPmshri.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-center text-purple-600 font-bold">{total.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-center">
                        {index > 0 && (
                          <span className={`flex items-center justify-center gap-1 ${parseFloat(growth) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {parseFloat(growth) > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            {growth}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div> */}

        {/* Schools Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Award className="w-5 h-5 text-green-600" />
                Highest Enrollment Schools
                {activeView === 'gender' && ' (by Gender)'}
                {activeView === 'stream' && ' (by Stream)'}
              </h3>
            </div>
            <div className="divide-y divide-gray-100 overflow-auto h-96">
              {schoolsLoading ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-green-600" />
                  <p>Loading schools...</p>
                </div>
              ) : schoolsData.top.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No enrollment data available</p>
                </div>
              ) : (
                schoolsData.top.map((item: any, index: number) => {
                  // Support both new API shape and old shape
                  const name  = item?.School?.Name    || item?.school?.schoolName || 'N/A';
                  const state = item?.State           || item?.school?.stateName  || 'N/A';
                  const ro    = item?.RO?.Name        || item?.school?.roName     || 'N/A';
                  const pmPhase = item?.PMShreePhase  || (item?.school?.isPMShree ? 'PM-SHRI' : '');
                  const total = +(item?.TotalStudents ?? item?.data?.totalEnrollment ?? 0);
                  const boys  = +(item?.Gender?.[0]   ?? item?.data?.boys  ?? 0);
                  const girls = +(item?.Gender?.[1]   ?? item?.data?.girls ?? 0);
                  const other = +(item?.Gender?.[2]   ?? 0);
                  const ssr   = item?.ClassWiseStudents?.SeniorSecondaryStage || {};
                  const sci   = (+(ssr.Class11?.Science    || 0)) + (+(ssr.Class12?.Science    || 0)) || item?.data?.science    || 0;
                  const com   = (+(ssr.Class11?.Commerce   || 0)) + (+(ssr.Class12?.Commerce   || 0)) || item?.data?.commerce   || 0;
                  const hum   = (+(ssr.Class11?.Humanities || 0)) + (+(ssr.Class12?.Humanities || 0)) || item?.data?.humanities || 0;
                  const voc   = (+(ssr.Class11?.Vocational || 0)) + (+(ssr.Class12?.Vocational || 0));
                  return (
                    <div key={item?.School?.ID || item?.school?.schoolID || index} className="px-6 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => { const id = item?.School?.ID || item?.school?.schoolID; if (id) setSelectedSchool({ schoolID: id, schoolName: name, infraData: { ...item?.data, schoolID: id, schoolName: name, state: state, region: ro }, tabLabel: 'Enrollment' }); }}>
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-50 text-gray-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-800">{name}</h4>
                            {pmPhase && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {pmPhase}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{state} • {ro}</p>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs">
                            <span className="font-semibold text-purple-600">{total.toLocaleString()} total</span>
                            {activeView === 'gender' && (
                              <>
                                <span className="text-blue-600">👦 {boys.toLocaleString()} Boys</span>
                                <span className="text-pink-600">👧 {girls.toLocaleString()} Girls</span>
                                {other > 0 && <span className="text-gray-500">Other: {other.toLocaleString()}</span>}
                              </>
                            )}
                            {activeView === 'stream' && (
                              <>
                                <span className="text-blue-600">🔬 {sci.toLocaleString()} Sci</span>
                                <span className="text-green-600">💼 {com.toLocaleString()} Com</span>
                                <span className="text-orange-600">📖 {hum.toLocaleString()} Hum</span>
                                {voc > 0 && <span className="text-indigo-600">🏭 {voc.toLocaleString()} Voc</span>}
                              </>
                            )}
                            {activeView === 'social' && (() => {
                              const sc = item?.SocialCategory || {};
                              return (
                                <>
                                  <span>Gen: {+(sc.General || 0)}</span>
                                  <span>OBC-CL: {+(sc.OBC_CL || 0)}</span>
                                  <span>SC: {+(sc.SC || 0)}</span>
                                  <span>ST: {+(sc.ST || 0)}</span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-orange-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-orange-600" />
                Lowest Enrollment Schools
                {activeView === 'gender' && ' (by Gender)'}
                {activeView === 'stream' && ' (by Stream)'}
              </h3>
            </div>
            <div className="divide-y divide-gray-100 overflow-auto h-96">
              {schoolsLoading ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-orange-600" />
                  <p>Loading schools...</p>
                </div>
              ) : schoolsData.bottom.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <TrendingDown className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No data available</p>
                </div>
              ) : (
                schoolsData.bottom.slice(0, 10).map((item: any, index: number) => {
                  const name   = item?.School?.Name   || item?.school?.schoolName || 'N/A';
                  const state  = item?.State          || item?.school?.stateName  || 'N/A';
                  const ro     = item?.RO?.Name       || item?.school?.roName     || 'N/A';
                  const pmPhase = item?.PMShreePhase  || (item?.school?.isPMShree ? 'PM-SHRI' : '');
                  const total  = +(item?.TotalStudents ?? item?.data?.totalEnrollment ?? 0);
                  const boys   = +(item?.Gender?.[0]   ?? item?.data?.boys  ?? 0);
                  const girls  = +(item?.Gender?.[1]   ?? item?.data?.girls ?? 0);
                  const other  = +(item?.Gender?.[2]   ?? 0);
                  const ssr    = item?.ClassWiseStudents?.SeniorSecondaryStage || {};
                  const sci    = (+(ssr.Class11?.Science    || 0)) + (+(ssr.Class12?.Science    || 0)) || item?.data?.science    || 0;
                  const com    = (+(ssr.Class11?.Commerce   || 0)) + (+(ssr.Class12?.Commerce   || 0)) || item?.data?.commerce   || 0;
                  const hum    = (+(ssr.Class11?.Humanities || 0)) + (+(ssr.Class12?.Humanities || 0)) || item?.data?.humanities || 0;
                  const voc    = (+(ssr.Class11?.Vocational || 0)) + (+(ssr.Class12?.Vocational || 0));
                  return (
                    <div key={item?.School?.ID || item?.school?.schoolID || index} className="px-6 py-4 hover:bg-orange-50 cursor-pointer" onClick={() => { const id = item?.School?.ID || item?.school?.schoolID; if (id) setSelectedSchool({ schoolID: id, schoolName: name, infraData: { ...item?.data, schoolID: id, schoolName: name, state: state, region: ro }, tabLabel: 'Enrollment' }); }}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-800">{name}</h4>
                            {pmPhase && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {pmPhase}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{state} • {ro}</p>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs">
                            <span className="font-semibold text-orange-600">{total.toLocaleString()} total</span>
                            {activeView === 'gender' && (
                              <>
                                <span className="text-blue-600">👦 {boys.toLocaleString()}</span>
                                <span className="text-pink-600">👧 {girls.toLocaleString()}</span>
                                {other > 0 && <span className="text-gray-500">Other: {other.toLocaleString()}</span>}
                              </>
                            )}
                            {activeView === 'stream' && (
                              <>
                                <span className="text-blue-600">🔬 {sci.toLocaleString()}</span>
                                <span className="text-green-600">💼 {com.toLocaleString()}</span>
                                <span className="text-orange-600">📖 {hum.toLocaleString()}</span>
                                {voc > 0 && <span className="text-indigo-600">🏭 {voc.toLocaleString()}</span>}
                              </>
                            )}
                            {activeView === 'social' && (() => {
                              const sc = item?.SocialCategory || {};
                              return (
                                <>
                                  <span>Gen: {+(sc.General || 0)}</span>
                                  <span>OBC-CL: {+(sc.OBC_CL || 0)}</span>
                                  <span>SC: {+(sc.SC || 0)}</span>
                                  <span>ST: {+(sc.ST || 0)}</span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <span className="text-sm text-red-600 font-medium flex items-center gap-1 ml-2">
                          <TrendingDown className="w-4 h-4" />
                          Low
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'Enrollment';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
